
import { Injectable } from '@angular/core';
import { IInitializePriceCalculator } from '../../model/models/price-calculator/initialize-price-calculator.interface';
import { LoggerProvider, LoggerLevel, LoggerEvent } from '../../providers/logger/logger';
import { APP_CONFIG } from '../../environment/';
import { IAnalyticsEvent } from '../../model/models/analytics';
import { ICustomer } from '../../model/models/customer';
import { Platform } from 'ionic-angular';
import { AppVersion } from '@ionic-native/app-version';
import { ProviderUtilities } from '../provider-utilities';
import { HttpProvider } from '../http/http';
import {LogRocketProvider} from '../../providers/log-rocket/log-rocket';
import { RouteProvider } from '../../providers/route/route';
import { StorageProvider } from '../storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { AnalyticsEventType, AnalyticsResetType, SegmentLocationNames } from '../../model/types/analytics.const';
import { PhoneProvider } from '../phone/phone';
import { getAnalyticsIds } from '../../providers/analytics/deeplink-source';
import { FeatureFlagTypes } from '../../model/types/feature-flag-types';

declare let Segment;
declare let analytics;
declare let window;

@Injectable()
export class SegmentProvider {
  version = '0.1';
  queuedIdentifyEvents = [];
  lastEventName;
  lastEventData;
  attributionData = {}
  userIdStr = null;
  calculator: IInitializePriceCalculator;
  logRocketIdentify: boolean = false;
  segmentPromise: any = null;
  identifyTimeout;
  lastIdentifyData;
  private identityReady: Promise<any>;
  private identityChain: Promise<any> = Promise.resolve();

  // OM-19023: late-bound BranchProvider reference (set via setBranchProvider) so we can push the
  // Ria customer ID to Branch.setIdentity() from the same chokepoint that identifies the user to
  // Amplitude. A direct import would create a circular dependency (BranchProvider already injects
  // SegmentProvider), mirroring the late-bound vueProvider pattern used elsewhere.
  private branchProvider: any = null;

  static amplitudeSessionId = '';
  static segmentAnonymousId;
  static initializedResolve;
  static initializedPromise: Promise<any> = new Promise((resolve) => { SegmentProvider.initializedResolve = resolve; });
  static isPilotUser: boolean = false;
  static currentUserId = '';

  onResume;
  fireAccessEvent = true;

  // static platformRef;
  // static routeProviderRef;

  constructor(
    private appVersion: AppVersion,
    private platform: Platform,
    private logRocket: LogRocketProvider,
    private storageProvider: StorageProvider,
    private routeProvider: RouteProvider,
    private phoneProvider: PhoneProvider) {
    // SegmentProvider.platformRef = platform;
    // SegmentProvider.routeProviderRef = routeProvider;

    StorageProvider.analytics = this;

    this.identityReady = this.platform.ready().then(async () => {
      if (this.platform.is('cordova')) {
        SegmentProvider.initializedResolve();
        this.version = await this.appVersion.getVersionNumber();
      }

      SegmentProvider.currentUserId = await this.storageProvider.get(StorageType.SegmentUserId);
      if (!SegmentProvider.currentUserId)
        SegmentProvider.currentUserId = '';

    }).catch((ex) => {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to restore analytics identity');
      if (!SegmentProvider.currentUserId)
        SegmentProvider.currentUserId = '';
    });

    this.identityReady.then(async () => {
      if (this.logRocket.logRocketEnterPromise) {
        await this.logRocket.logRocketEnterPromise;
        this.logAmplitudeEvent(this.logRocket.segmentSessionURL);
      }
    }).catch((ex) => {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to log LogRocket session URL to analytics');
    });
  }

  async init() {
    if (this.platform.is('cordova')) {
      // setting 11 minutes - 11×60×1000 = 660,000 milliseconds.
      try {
       const timeout = APP_CONFIG.amplitudeSessionTimeout || 660000;
        Segment.setAmplitudeMinTimeBetweenSessionsMillis(timeout, () => {
          LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.SegmentInit, 'Set Amplitude min time between sessions', 11);
        }, () => {
          LoggerProvider.warning(this, LoggerEvent.PluginError, 'Failed to set Amplitude min time between sessions');
        });
      }catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to set Amplitude min time between sessions');
      }

      // TODO !!!!!!!!!!!!!!!!!!!! align !!!!!!!!!!!!!!!
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.SegmentInit, 'Segment native initialized.');
      try {
        Segment.start({
          context: {
            app: {
              businessUnit: APP_CONFIG.analyticsSettings.businessUnit,
              platform: this.getPlatformName(),
              version: this.version,
            },
          },
        });
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to start Segment');
      }
    } else {
      const analytics = window.analytics = window.analytics || [];
      this.segmentPromise = new Promise((resolve, reject) => {
        analytics.invoked = true;
        analytics.methods = APP_CONFIG.analyticsSettings.segment.methods;
        analytics.factory = (t) => {  return function () {
          const e = Array.prototype.slice.call(arguments);
          e.unshift(t); analytics.push(e); return analytics
        }
        };
        for (let t = 0; t < analytics.methods.length; t++) {
          const e = analytics.methods[t];
          analytics[e] = analytics.factory(e)
        }

        analytics.SNIPPET_VERSION = '4.1.0';
        const url = `${APP_CONFIG.analyticsSettings.segment.baseUrl}${APP_CONFIG.analyticsSettings.segment.apiKey}/analytics.min.js`;
        LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.SegmentInit, 'Load segment scripts', url);
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = url;
        script.onload = () => {
          resolve(true);
          LoggerProvider.info(this, 'Segment initialized');
        };
        script.onerror = (err) => {
          reject();
          LoggerProvider.exception(this, err, LoggerEvent.ProviderException, 'Failed to initalize Segment !');
        };
        document.body.appendChild(script);
      });
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.SegmentInit, 'Segment native skipped, cordova not available.');
    }
    SegmentProvider.initializedResolve();
  }

  async reset() {
    window.clearTimeout(this.identifyTimeout);
    this.lastIdentifyData = null;
    this.userIdStr = null;
    SegmentProvider.currentUserId = '';
    try {
      await this.storageProvider.set(StorageType.SegmentUserId, '');
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to clear stored analytics user id');
    }
    if (this.platform.is('cordova')) {
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Segment native reset');

      Segment.reset();
    }
    else if (this.segmentPromise) {
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Segment debug reset');
      this.segmentPromise.then(() => {
        analytics.reset();
      })
    }
  }

  flush() {
    if (this.platform.is('cordova')) {
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Segment native flush');

      Segment.flush();
    }
    else
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Segment debug flush');
  }

  setBranchProvider(branchProvider: any) {
    this.branchProvider = branchProvider;
  }

  private async runIdentityTransition<T>(transition: () => Promise<T>): Promise<T> {
    const run = this.identityChain.then(async () => {
      await this.identityReady;
      return transition();
    });
    this.identityChain = run.catch(() => null);
    return run;
  }

  private async resetIdentity(resetType: AnalyticsResetType, enteringRegistration = false) {
    let previousAnonymousId = SegmentProvider.segmentAnonymousId;
    if (this.platform.is('cordova'))
      previousAnonymousId = await this.getAnonymousId().catch(() => previousAnonymousId);
    await this.reset();
    await this.trackUserReset(previousAnonymousId, resetType, enteringRegistration).catch((ex) => {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to track analytics user reset');
    });
  }

  identifiedUserId(): string {
    const userId = `${SegmentProvider.currentUserId || ''}`;
    return userId === '0' ? '' : userId;
  }

  async beginRegistration() {
    try {
      await this.runIdentityTransition(async () => {
        if (this.identifiedUserId())
          await this.resetIdentity(AnalyticsResetType.UserToAnonymous, true);
      });
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to reset analytics identity for registration');
    }
  }

  async resetForUserChange() {
    try {
      await this.runIdentityTransition(async () => {
        if (this.identifiedUserId())
          await this.resetIdentity(AnalyticsResetType.UserToUser);
        else
          await this.reset();
      });
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to reset analytics identity for user change');
    }
  }

  async identify(userId: any = '', traits = {}) {
    try {
      await this.runIdentityTransition(async () => this.applyIdentity(userId, traits));
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to identify analytics user');
    }
  }

  private async applyIdentity(userId: any, traits: any) {
    // if we already identified a user and then we call an identify without a user we have to reset the session
    // UPDATE:
    // the problem is bigger than just session since Segment has buggy behaviour
    // basically already doing a login and then calling identify, then closing the app and opening again, going through registration
    // and calling identify with empty userId can already update the email since the anonymous IDs are the same although the userIds
    // don't match at all
    // worse is that when doing a login again Segment seems to not update the email anymore and stick with the old, wrong one
    // CONCLUSION: hence we store the last used userId and reset when it differs
    //
    // details from chat:
    // since all of that is not great but would not have resulted in a problem since when the user logins we call identify again.
    // so normally:
    // - the user would have logged in
    // - identify would have been called with his real email
    // - the email would have been updated
    // - the user creates a transaction
    // - the email would have been sent to the correct address !
    // - hence normally no problem !
    // but that didn't work since like explained above segment refused to update his email again and sticked with the old one
    // at the moment we can't trust segment to handle email updates properly
    //
    // ensure they are compared as strings
    let compareUserId = userId;
    if (!compareUserId)
      compareUserId = '0';
    const previousUserId = this.identifiedUserId();
    if (previousUserId && previousUserId !== `${compareUserId}`) {
      LoggerProvider.info(this, `Analytics user ID changed from ${previousUserId} to ${compareUserId}`);
      await this.resetIdentity(this.getResetType(true, `${compareUserId}`));
    }
    SegmentProvider.currentUserId = compareUserId;
    this.storageProvider.set(StorageType.SegmentUserId, SegmentProvider.currentUserId);

    if (traits)
      this.cleanIdentifyTraits(traits);

    if (userId) {
      // if we are logged in let's delay the identify call a bit to see if another change has been applied (i.e. changing notifcations can quickly create several identify calls)
      window.clearTimeout(this.identifyTimeout);
      this.identifyTimeout = window.setTimeout(() => {
        this.executeIdentify(userId, traits);
      }, 1000);
    } else
      this.executeIdentify(userId, traits);
  }

  executeIdentify(userId: any = '', traits = {}) {
    // OM-19023: mirror the Amplitude user_id into Branch's developer_identity so paid-acquisition
    // exports can be joined 1:1 to Amplitude. userId here is the Ria customer ID — the same value
    // sent to Amplitude below. BranchProvider dedupes and no-ops off-device, so this is safe to call
    // on every identify (login, registration, settings). Fire-and-forget: it awaits Branch init internally.
    if (userId && this.branchProvider)
      this.branchProvider.setIdentity(userId);

    if (userId) {
      let userIdOrig = this.userIdStr;
      this.userIdStr = userId.toString()

      if (userIdOrig === this.userIdStr && this.lastIdentifyData) {
        const identifyData = { ...this.lastIdentifyData, ...traits }; // need to combine them since eventually additional traits have been fired just once
        if (JSON.stringify(this.lastIdentifyData) === JSON.stringify(identifyData)) {
          LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Duplicate identify event ignored ' + userId);
          this.logQueedEvents(true);
          return;
        }
        this.lastIdentifyData = identifyData;
      }
      else
        this.lastIdentifyData = traits;
    }
    if (this.platform.is('cordova')) {
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Segment native tracking identify user ' + userId, traits);

      if (userId)
        this.userIdStr = userId.toString()
      Segment.identify(this.userIdStr, traits, this.getOptions());
      this.logQueedEvents();
    }
    else {
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Tracking, 'Segment debug tracking identify user ' + userId, traits);
      this.segmentPromise.then(() => {
        analytics.identify(this.userIdStr, traits, this.getOptions());
        this.logQueedEvents();
      });
    }
    this.flush();
  }

  getPhoneTraits(customer: ICustomer)
  {
    let phoneNumber: any = null;
    let phoneCountry = null;
    if (customer.mobilePhone && customer.mobilePhone.number) {
      phoneNumber = this.phoneProvider.formatPhoneNumber(customer.mobilePhone.countryCode, customer.mobilePhone.number).international;
      phoneCountry = customer.mobilePhone.countryCode;
    } else if (customer.homePhone && customer.homePhone.number) {
      phoneNumber = this.phoneProvider.formatPhoneNumber(customer.homePhone.countryCode, customer.homePhone.number).international;
      phoneCountry = customer.homePhone.countryCode;
    }
    else if (customer.phoneNumber) {
      phoneNumber = customer.phoneNumber.replace('-', '');
      phoneCountry = customer.phoneNumberCountry;
    }
    if (phoneNumber && phoneNumber.length > 0 && phoneNumber[0] !== '+')
      phoneNumber = '+' + phoneNumber;
    return {
      phoneNumber,
      phoneCountry
    }
  }

  getIdentifyTraits(customer: ICustomer, liteCustomer, setMarketing: boolean = false, skipMarketing: boolean = false) {
    // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!
    // VERY IMPORTANT
    // Ensure all traits of customer are also supported in login-provider.ts identifyUser() !
    // !!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!

    const attributionData = this.attributionData;
    let devicePixelRatio = null;

    if (this.platform.is('cordova') && window.plugins && window.plugins.DevicePixelRatio) {
      window.plugins.DevicePixelRatio.getDevicePixelRatio(
        (ratio: number) => {
          const value = Number(ratio);
          devicePixelRatio = value;
          LoggerProvider.info(this, 'Successfully computed device pixel ratio' + devicePixelRatio);
        },
        (err: any) => {
          LoggerProvider.log(this, LoggerLevel.Error, LoggerEvent.GeneralException, 'Failed to get native device pixel ratio', err);
          const fallback = Number((window.devicePixelRatio || 1).toFixed(2));
          LoggerProvider.info(this, 'Switching to fallback due to an error. Device pixel ratio' + fallback);
          devicePixelRatio = fallback;
        }
      );
    } else {
      // Browser/dev fallback
      const fallback = Number((window.devicePixelRatio || 1).toFixed(2));
      LoggerProvider.info(this, 'Switching to Browser/dev fallback. Device pixel ratio' + fallback);
      devicePixelRatio = fallback;
    }

    let email = customer.emailAddress;
    if (!email && customer.id)
      email = customer.id + '@placeholder.email';

    let traits: any = {
      email,
      userId: customer.id,
      userLanguage: HttpProvider.currentLanguage,
      locale: HttpProvider.currentLanguage,
      product: 'App',
      productVariant: 'OneApp',
      brand: [ APP_CONFIG.branding.segmentProduct ],
      pilotUser: SegmentProvider.isPilotUser,
      devicePixelRatio: devicePixelRatio,
      accountCountry: HttpProvider.authCountry,
      ...attributionData,
    };
    if (!(skipMarketing && liteCustomer)) {
      traits = {
        ...traits,
        // marketingNotifAllowed: (liteCustomer) ? setMarketing : customer.willReceiveSmsMarketing, // will be removed, for backward compitability
        marketingPushEnabled: (liteCustomer) ? setMarketing : customer.willReceivePushMarketing,
        marketingMailEnabled: (liteCustomer) ? setMarketing : customer.willReceiveEmailMarketing,
        transactionalPushEnabled: (liteCustomer) ? true : customer.willReceivePushNotifications,
      };
    }
    if (!liteCustomer) {
      const phoneTraits = this.getPhoneTraits(customer);
      traits = {
        ...traits,
        ...phoneTraits,
        firstName: customer.firstName,
        lastName: customer.lastName,
        dateOfBirth: customer.dateOfBirth,
        country: (customer.address ? customer.address.country : null),
        age: (customer.dateOfBirth ? ProviderUtilities.getAge(ProviderUtilities.parseDate(customer.dateOfBirth)) : null),
        city: (customer.address ? customer.address.city : null),
        state: (customer.address ? customer.address.stateProvince : null),
        accountStatus: customer.isDisabled ? 'disable' : 'active', // TODO do we really get disabled accounts ?
      };
    }

    if(customer.marketingConsentType){
     traits = {
      ...traits,
      marketingConsentsType: customer.marketingConsentType, // IMPORTANT: marketingConsentType can't be used as it has been wrongly created in Iterable as a long in Staging hence all identify calls would fail in Iterable - unfortunately for whatever reason Iterable doesn't allow to change or delete a user field once created !
     }
    }

    if(typeof customer.legitimateInterest === "boolean"){
      traits = {
        ...traits,
        legitimateInterestOnly: customer.legitimateInterest,
       }
    }

    if(typeof customer.willReceiveServiceEmailMessages === "boolean"){
      traits = {
        ...traits,
        serviceMailEnabled: customer.willReceiveServiceEmailMessages,
       }
    }

    if(typeof customer.willReceiveServicePushMessages === "boolean"){
      traits = {
        ...traits,
        servicePushEnabled: customer.willReceiveServicePushMessages,
       }
    }

    if (customer.customerForeignIpInformation && customer.customerForeignIpInformation.foreignRegistrationCountryIso) {
      traits = {
        ...traits,
        registrationIPCountry: customer.customerForeignIpInformation.foreignRegistrationCountryIso,
      };
    }

    return traits;
  }

  getLogRocketTraits(customer: ICustomer, liteCustomer, setMarketing: boolean = false, skipMarketing: boolean = false) {
    let traits: any = {
      email: customer.emailAddress,
      userId: customer.id,
      userLanguage: HttpProvider.currentLanguage,
      locale: HttpProvider.currentLanguage,
      product: 'App',
      productVariant: 'OneApp',
      brand: [ APP_CONFIG.branding.segmentProduct ],
      transactionalPushEnabled: (liteCustomer) ? true : customer.willReceivePushNotifications,
      pilotUser: SegmentProvider.isPilotUser,
    };
    if (!(skipMarketing && liteCustomer)) {
      traits = {
        ...traits,
        // marketingNotifAllowed: (liteCustomer) ? setMarketing : customer.willReceivePushMarketing, // will be removed, for backward compitability
        marketingPushEnabled: (liteCustomer) ? setMarketing : customer.willReceivePushMarketing,
        marketingMailEnabled: (liteCustomer) ? setMarketing : customer.willReceiveEmailMarketing,
      };
    }
    if (!liteCustomer) {
      const phoneTraits = this.getPhoneTraits(customer);
      traits = {
        ...traits,
        ...phoneTraits,
        name: `${customer.firstName} ${customer.lastName}`,
        dateOfBirth: customer.dateOfBirth,
        country: customer.countryOfBirth,
        email: customer.emailAddress,
        userId: customer.id,
        age: (customer.dateOfBirth ? ProviderUtilities.getAge(ProviderUtilities.parseDate(customer.dateOfBirth)) : null),
        city: (customer.address ? customer.address.city : null),
        state: (customer.address ? customer.address.stateProvince : null),
        userLanguage: HttpProvider.currentLanguage,
        locale: HttpProvider.currentLanguage,
        product: 'App',
        productVariant: 'OneApp',
        brand: [ APP_CONFIG.branding.segmentProduct ],
      }
    }
    return traits;
  }

  identifyCustomerByAnalyticsData(analyticsData, includeNotificationPreferences, additionalTraits = {}) {
    let profile = {
      id: analyticsData.id,
      phoneNumberCountry: analyticsData.phoneNumberCountry ? analyticsData.phoneNumberCountry : analyticsData.phoneCountry,
      emailAddress: analyticsData.email,
      firstName: analyticsData.firstName,
      lastName: analyticsData.lastName,
      dateOfBirth: analyticsData.dateOfBirth,
      address: {
        country: APP_CONFIG.region.defaultCountryFrom,
        type: '',
        city: analyticsData.city,
        street: analyticsData.address,
        stateProvince: analyticsData.state,
        postCode: analyticsData.zipCode,
      },
      ...analyticsData,
      phoneNumber: analyticsData.mobilePhone ? analyticsData.mobilePhone : analyticsData.phoneNumberFull,
    };
    if (includeNotificationPreferences) {
      profile = {
        ...profile,
        willReceivePushMarketing: (analyticsData.marketingPushEnabled || analyticsData.receivePushMarketing) ? true : false,
        willReceiveEmailMarketing: (analyticsData.marketingMailEnabled || analyticsData.receiveEmailMarketing) ? true : false,
        willReceivePushNotifications: profile.transactionPushEnabled
      };
    } else {
      // to be on the safe side if it shouldn't be included delete everything related to notification preferences since it could by in analytics data
      delete profile.receivePushMarketing;
      delete profile.receiveEmailMarketing;
      delete profile.marketingPushEnabled;
      delete profile.marketingMailEnabled;
      delete profile.willReceivePushMarketing;
      delete profile.willReceiveEmailMarketing;
      delete profile.transactionPushEnabled;
    }
    return this.identifyCustomer(profile, additionalTraits)
  }

  identifyCustomer(customer: ICustomer, additionalTraits = {}) {
    const traits = this.getIdentifyTraits(customer, false);
    this.identify(customer.id, { ...traits, ...additionalTraits });

    try {
      const logRocketTraits = this.getLogRocketTraits(customer, false);
      this.logRocketIdentify = true;
      this.logRocket.identify(customer.id, { ...logRocketTraits, ...additionalTraits });
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to call identify for logrocket');
    }
  }

  identifyLiteCustomer(customer, setMarketing: boolean = false, skipMarketing: boolean = false, additionalTraits = {}) {
    const traits = this.getIdentifyTraits(customer, true, setMarketing, skipMarketing);
    this.identify(customer.id, { ...traits, ...additionalTraits });

    try {
      const logRocketTraits = this.getLogRocketTraits(customer, true, setMarketing, skipMarketing);
      this.logRocketIdentify = true;
      this.logRocket.identify(customer.id, { ...logRocketTraits, ...additionalTraits });
    }
    catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.ProviderException, 'Failed to call identify for logrocket');
    }
  }

  getLastEventName() {
    return this.lastEventName;
  }

  processEvent(sessionId) {
    const eventName = this.getLastEventName();

    if (!this.platform.is('cordova') && sessionId && sessionId.event) {
      if (sessionId.event.anonymousId)
        SegmentProvider.segmentAnonymousId = sessionId.event.anonymousId;
      if (sessionId.event.integrations && sessionId.event.integrations['Actions Amplitude'] && sessionId.event.integrations['Actions Amplitude'].session_id)
        sessionId = sessionId.event.integrations['Actions Amplitude'].session_id.toString();
    }
    
    SegmentProvider.amplitudeSessionId = sessionId;
    LoggerProvider.amplitudeSessionId = sessionId;
    if (!this.logRocketIdentify) {
      this.logRocketIdentify = true;
      this.logRocket.identify();
    }
    this.logRocket.track(eventName, this.lastEventData);
  }

  logAmplitudeEvent(sessionURL) {
    if (this.platform.is('cordova')) {
      Segment.logAmplitudeEvent({ 'sessionURL': sessionURL });
    }
  }

  async trackEvent(event: IAnalyticsEvent, recordTimestamp: boolean = false) {
    if (event.eventName === AnalyticsEventType.RegistrationStarted)
      await this.beginRegistration();
    if (event.analyticsData)
      this.cleanAnalyticsData(event.analyticsData);
    if (this.platform.is('cordova')) {
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Tracking event', event);
      this.lastEventName = event.eventName;
      if (!event.analyticsData)
        event.analyticsData = {};
      this.lastEventData = event.analyticsData;
      event.analyticsData.product = "App";
      event.analyticsData.logRocketSessionURL = this.logRocket.getSegmentSessionURL();
      event.analyticsData.brand = APP_CONFIG.branding.segmentProduct;
      event.analyticsData.productVariant = event.analyticsData.productVariant || 'OneApp';

      const analyticsIds = getAnalyticsIds();

      if (analyticsIds.deviceId) {
        event.analyticsData.deviceId = analyticsIds.deviceId;
      }
      if (analyticsIds.anonymousId) {
        event.analyticsData.anonymousId = analyticsIds.anonymousId;
      }

      if(recordTimestamp) {
        const analyticsData = event.analyticsData;
        event.analyticsData = {
          localTime: this.getHour(),
          localDay: this.getDayAsString(),
          ...analyticsData
        };
      }
      Segment.track(event.eventName, this.getEventAnalyticsData(event.analyticsData), this.getOptions(),        (sessionId) => {
          if (sessionId)
            this.processEvent(sessionId);
        }
      );
      // this.firebase.logMessage(`customerId: ${this.userIdStr} action: ${event.eventName}`);
    }
    else // warning so it is visible well
    {
      this.lastEventName = event.eventName;
      if (!event.analyticsData)
        event.analyticsData = {};
      this.lastEventData = event.analyticsData;
      if (!LoggerProvider.amplitudeSessionId)
        LoggerProvider.amplitudeSessionId = (new Date().getTime()).toString();
      this.processEvent(LoggerProvider.amplitudeSessionId);
      LoggerProvider.log(this, LoggerLevel.Warning, LoggerEvent.Tracking, 'Tracking debug event' + JSON.stringify(event), event);
      await this.segmentPromise;
      event.analyticsData.logRocketSessionURL = this.logRocket.getSegmentSessionURL();
      event.analyticsData.brand = APP_CONFIG.branding.segmentProduct;
      event.analyticsData.productVariant = event.analyticsData.productVariant || 'OneApp';

      const analyticsIds = getAnalyticsIds();
      if (analyticsIds.deviceId) {
        event.analyticsData.deviceId = analyticsIds.deviceId;
      }
      if (analyticsIds.anonymousId) {
        event.analyticsData.anonymousId = analyticsIds.anonymousId;
      }


      analytics.track(event.eventName, this.getEventAnalyticsData(event.analyticsData), this.getOptions(),
        (sessionId) => {
          if (sessionId)
            this.processEvent(sessionId);
          else
            this.processEvent(LoggerProvider.sessionId);
        }
      );
    }
  }

  async trackView(event: IAnalyticsEvent) {
    if (this.platform.is('cordova')) {
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Tracking view', event);
      if (!event.analyticsData)
        event.analyticsData = {};
      Segment.page(event.eventName, event.analyticsData, this.getOptions(),
        (sessionId) => {
          this.processEvent(sessionId);
        }
      );
    }
    else {
      LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Tracking, 'Tracking debug view', event);
      await this.segmentPromise;
      analytics.page(event.eventName, event.analyticsData, this.getOptions(),
        (sessionId) => {
          if (sessionId)
            this.processEvent(sessionId);
          else
            this.processEvent(LoggerProvider.sessionId);

        }
      );
    }
  }

  getDayAsString() {
    var weekday=new Array(7);
    weekday[0]="Sunday";
    weekday[1]="Monday";
    weekday[2]="Tuesday";
    weekday[3]="Wednesday";
    weekday[4]="Thursday";
    weekday[5]="Friday";
    weekday[6]="Saturday";
    var date = new Date();
    var day = date.getDay();
    return weekday[day];
  }

  getHour() {
    var date = new Date();
    var hour = date.getHours();
    return hour;
  }

  consentIDFA(): Promise<any> {
    return new Promise(async (resolve)=> {
      await this.platform.ready();
      if (this.platform.is('cordova')) {
        LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Showing ATT popup');
        Segment.consentIDFA(
          (res)=> resolve(res),
          () => resolve(false)
        );
      } else {
        resolve(true);
      }
    })
  }

  firebaseAccessDenied(): Promise<any> {
    return new Promise(async (resolve)=> {
      await this.platform.ready();
      if (this.platform.is('cordova')) {
        LoggerProvider.log(this, LoggerLevel.Info, LoggerEvent.Info, 'Denying firebase permission');
        Segment.firebaseConsentDenied();
      }
      resolve(true);
    })
  }

  async queueIdentifyEvent(event: IAnalyticsEvent) {
    this.queuedIdentifyEvents.push(event);
  }

  getPlatformName() {
    let platformName = 'unknown';
    if (this.platform.is('ios')) platformName = 'ios';
    else if (this.platform.is('android')) platformName = 'android';
    return platformName;
  }

  getEventAnalyticsData(analyticsData) {
    if (HttpProvider.hasAuthCustomerId()) {
      analyticsData.userId = HttpProvider.getAuthCustomerId();
    } else {
      analyticsData.tempUserId = (HttpProvider.parsedAuthToken) ? HttpProvider.parsedAuthToken.ReservedCustomerId : null;
    }

    return analyticsData;
  }

  getOptions() {
    return {
      context: {
        app: {
          businessUnit: APP_CONFIG.analyticsSettings.businessUnit,
          platform: this.getPlatformName(),
          version: this.version,
          brand: APP_CONFIG.branding.segmentProduct
        },
        device: {
          id: LoggerProvider.uuid
        }
      },
    };
  }

  static formatNumber(value) {
    if (value) return parseFloat(value.toString());
    return value;
  }

  static formatInt(value) {
    if (value) return parseInt(value.toString());
    return value;
  }

  async getAnonymousId() {
    return new Promise(
      (resolve, reject) => {
        Segment.anonymousId(
          (anonymousId) => {
            SegmentProvider.segmentAnonymousId = anonymousId;
            resolve(anonymousId)
          },
          () => reject(false)
        )
      }
    )
  }

  // it seems that Segment has problems with passing directly boolean values to them - we can see that they get to them in the raw data but
  // in the pretty data view it's missing then and hence doesn't seem to get dispatched to other destinations i.e. legitimateInterestOnly to Iterable
  cleanAnalyticsData(analyticsData) {
    if (analyticsData) {
      for (const key in analyticsData) {
        let value = analyticsData[key];
        if (typeof(value) === 'boolean') {
          value = (value) ? 'true' : 'false';
          analyticsData[key] = value;
        }
      }
    }
  }

  // if you have an identify trait with null it will be passed as 'null' to Segment which i.e. can cause problems with legitimateInterestOnly since Iterable
  // expected a boolean value
  cleanIdentifyTraits(traits) {
    if (traits) {
      for (const key in traits) {
        let value = traits[key];
        if (value === null)
          delete traits[key]; // remove it completly so it is not passed as 'null'
      }
    }
  }

  logQueedEvents(canFlush = false){
    for (let i = 0; i < this.queuedIdentifyEvents.length; i++) {
      this.trackEvent(this.queuedIdentifyEvents[i]);
    }
    this.queuedIdentifyEvents = [];
    if(canFlush){
      this.flush();
    }
  }

  async setOnResume(fun: Function, module) {
    this.removeOnResume();
    let pageName = await this.routeProvider.getCurrentPageName();
    this.onResume =  this.platform.resume.subscribe(async () => {
      const currentPageName = await this.routeProvider.getCurrentPageName();
      if (pageName === currentPageName) {
        fun(module);
      }
    });
  }

  removeOnResume() {
    if (this.onResume) {
      this.onResume.unsubscribe();
      this.onResume = undefined;
    }
  }

  getResetType(hadIdentifiedUser: boolean, newUserId: string): AnalyticsResetType {
    if (!hadIdentifiedUser)
      return AnalyticsResetType.AnonymousToUser;
    return newUserId === '0' ? AnalyticsResetType.UserToAnonymous : AnalyticsResetType.UserToUser;
  }

  async trackUserReset(previousAnonymousId, resetType: AnalyticsResetType, enteringRegistration = false) {
    let resetReason = 'Login';
    let location = SegmentLocationNames.Login;
    if (enteringRegistration ||
        this.routeProvider.hasPage(this.routeProvider.pages.RlUserCountryPage) ||
        this.routeProvider.hasPage(this.routeProvider.pages.UserCountryPage) ||
        this.routeProvider.hasPage(this.routeProvider.pages.RegistrationStep1Page) ||
        this.routeProvider.hasPage(this.routeProvider.pages.RegistrationStepUsernamePage) ||
        this.routeProvider.hasPage(this.routeProvider.pages.RlCreateAccountPage)) {
      resetReason = 'Entering Registration';
      location = SegmentLocationNames.Registration;
    }

    const anonymouseId = await this.getAnonymousId();
    this.trackEvent({
      eventName: AnalyticsEventType.UserPropertiesReset,
      analyticsData: {
        previousAnonymousId,
        newAnonymousId: anonymouseId,
        resetReason,
        resetType,
        location,
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/analytics/segment.ts